/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Standard button component
 *
 * @author Vladislav Oshkanov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.1.0
 */

import { useCallback, memo } from 'react';
import type { MouseEvent, ReactNode } from 'react';

import type { TButtonKind } from '@/main/types/globalTypes';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';

import { ButtonContainerStyled, ButtonStyled } from './styled/ButtonStyled';

export interface IButtonProps {
    children?: ReactNode;
    className?: string;
    dataName?: string;
    disabled?: boolean;
    height?: string;
    kind: TButtonKind;
    onboarding?: EOnboardingComponentsId;
    onClick: (event: MouseEvent) => void;
    onMouseDown?: (event: MouseEvent) => void; // on middle mouse click
    type?: 'button' | 'submit' | 'reset' | undefined;
}

export const Button = (props: IButtonProps) => {

    const { disabled, onboarding, onClick } = props;

    const onClickHandler = useCallback((event: MouseEvent) => {
        if (!disabled) {
            onClick(event);
        }
    }, [disabled, onClick]);

    return (
        <ButtonContainerStyled className={`button ${props.className || ''}`}
                               data-name={props.dataName}
                               onClick={onClickHandler}
                               onMouseDown={props.onMouseDown}
                               {...{ [ONBOARDING_DATA_ATTR]: onboarding }}
        >
            <ButtonStyled
                className={props.disabled ? 'disabled' : ''}
                data-button={props.dataName}
                height={props.height}
                kind={props.kind}
                type={props.type}
            >
                {props.children}
            </ButtonStyled>
        </ButtonContainerStyled>
    );
};

Button.defaultProps = {
    kind: 'mediumNormal',
    onClick: () => {
    },
};

export default memo(Button);
